import React, {useEffect, useRef, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import GenerateMicroreelStore from '../../TapyblAI/GenerateMicroreel/store/GenerateMicroreelStore';
import {
  StoredGenerationInfo,
  clearGenerationInfo,
  readGenerationInfo,
  readGenerationResult,
  readLatestGenerationInfo,
} from './generatingTypes';
import {generatingMicroreelText} from './generatingMicroreelText';
import {generateMicroreelText} from '../../TapyblAI/GenerateMicroreel/generateMicroreelText';
import ConfirmationModal from '../../../uiToolkit/ConfirmationModal';
import {toast} from 'react-toastify';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import styles from './GeneratingMicroreel.module.css';

interface Props {
  generateMicroreelStore?: GenerateMicroreelStore;
}

const STEPS = [
  generatingMicroreelText.stepContentAnalyzed,
  generatingMicroreelText.stepScriptWritten,
  generatingMicroreelText.stepRenderingScenes,
  generatingMicroreelText.stepAddingNarration,
];

// Steps are timed; completion is detected by polling the list.
const STEP_DURATION_MS = 50000;
const POLL_INTERVAL_MS = 20000;
// Every poll error resolves quietly, so a few in a row is the only signal that
// the screen has stopped hearing from the backend rather than still waiting.
const CHECK_FAILURE_LIMIT = 3;

const renderSetting = (label: string, value: string, hint: string) => (
  <div className={styles.settingField} key={label}>
    <span className={styles.settingLabel}>{label}</span>
    <span className={styles.settingValue}>{value}</span>
    <span className={styles.settingHint}>{hint}</span>
  </div>
);

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="9.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M7 11.2l2.6 2.6L15 8.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GeneratingMicroreel = inject('generateMicroreelStore')(
  observer(({generateMicroreelStore}: Props) => {
    const {organizationId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const routed = location.state as StoredGenerationInfo | null;
    const [info] = useState<StoredGenerationInfo | null>(
      () =>
        readGenerationInfo(organizationId, routed?.id ?? undefined) ??
        readLatestGenerationInfo(organizationId) ??
        routed ??
        null,
    );
    const startedAt = info?.startedAt;
    const elapsedSteps = startedAt
      ? Math.min(
          Math.floor((Date.now() - startedAt) / STEP_DURATION_MS),
          STEPS.length - 1,
        )
      : 0;
    const [activeStep, setActiveStep] = useState(Math.max(0, elapsedSteps));
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [checkFailures, setCheckFailures] = useState(0);
    const [recheck, setRecheck] = useState(0);
    const leaveTarget = useRef<{groupId?: string}>();
    const canCancel = !!info;

    const projectPath = info?.projectId
      ? `/manage/${organizationId}/projects/${info.projectId}?tab=microreels`
      : undefined;

    const goToMicroreels = () => {
      navigate(projectPath ?? `/manage/${organizationId}/microreels`, {
        replace: true,
      });
    };

    // cancel_generation stops every running microreel generation of the org,
    // so the whole tracked list is dropped.
    const onConfirmCancel = async () => {
      if (!organizationId || canceling) return;
      setCanceling(true);
      const success =
        await generateMicroreelStore!.cancelGeneration(organizationId);
      setCanceling(false);
      setConfirmCancelOpen(false);
      if (success) {
        clearGenerationInfo(organizationId);
        goToMicroreels();
      }
    };

    useEffect(() => {
      if (organizationId && !info) goToMicroreels();
    }, [organizationId, !info]);

    useEffect(() => {
      const stepTimer = setInterval(() => {
        setActiveStep(step => Math.min(step + 1, STEPS.length - 1));
      }, STEP_DURATION_MS);
      return () => clearInterval(stepTimer);
    }, []);

    useEffect(() => {
      if (!organizationId) return;
      let finishing = false;
      const leave = (groupId?: string) => {
        finishing = true;
        leaveTarget.current = {groupId};
        if (projectPath) {
          navigate(
            groupId
              ? `/manage/${organizationId}/microreels/${groupId}`
              : projectPath,
            {replace: true, state: groupId ? {backPath: projectPath} : null},
          );
          return;
        }
        navigate(`/manage/${organizationId}/microreels`, {
          replace: true,
          state: groupId ? {openGroupId: groupId} : null,
        });
      };
      const settle = async () => {
        if (leaveTarget.current) {
          leave(leaveTarget.current.groupId);
          return;
        }
        if (finishing) return;
        const mine = info?.id;
        // Another tab may have settled this one and dropped it from the list.
        const settled = readGenerationResult(organizationId, mine);
        let groupId = settled?.groupId;
        let failed = !!settled?.failed;
        if (!settled) {
          const result =
            await generateMicroreelStore!.settleGenerations(organizationId);
          if (finishing) return;
          setCheckFailures(count => (result.checkFailed ? count + 1 : 0));
          const recovered = readGenerationResult(organizationId, mine);
          groupId =
            result.finished.find(item => item.info.id === mine)?.groupId ??
            recovered?.groupId;
          failed =
            result.failed.some(item => item.id === mine) || !!recovered?.failed;
          if (
            !groupId &&
            !failed &&
            !result.pending.some(item => item.id === mine)
          ) {
            leave();
            return;
          }
        }
        if (!groupId && !failed) return;
        if (failed) {
          toast.error(generatingMicroreelText.generationFailed, toastConfig);
          leave();
          return;
        }
        leave(groupId);
      };
      // Otherwise an already finished generation waits a full interval.
      settle();
      const poll = setInterval(settle, POLL_INTERVAL_MS);
      return () => {
        finishing = true;
        clearInterval(poll);
      };
    }, [organizationId, info?.id, recheck]);

    const onRecheck = () => {
      setCheckFailures(0);
      setRecheck(attempt => attempt + 1);
    };

    const renderStep = (step: string, index: number) => {
      const isDone = index < activeStep;
      const isActive = index === activeStep;
      return (
        <div className={styles.step} key={step}>
          {isDone ? (
            <span className={styles.stepDone}>
              <CheckIcon />
            </span>
          ) : isActive ? (
            <span className={styles.stepSpinner} />
          ) : (
            <span className={styles.stepPending} />
          )}
          <span
            className={`${styles.stepLabel} ${
              isDone ? styles.stepLabelDone : ''
            } ${!isDone && !isActive ? styles.stepLabelPending : ''}`}>
            {step}
          </span>
        </div>
      );
    };

    return (
      <div className={styles.container}>
        <button className={styles.backLink} onClick={goToMicroreels}>
          ← {info?.projectName ?? generatingMicroreelText.allMicroreels}
        </button>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            {info?.title ?? generatingMicroreelText.fallbackTitle}
          </h1>
          <span className={styles.generatingBadge}>
            {generatingMicroreelText.generating}
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.phonePreview}>
            <div className={styles.phoneCard}>
              <span className={styles.phoneSpinner} />
            </div>
            <div className={styles.filmStrip}>
              <span className={styles.filmFrameSide} />
              <span className={styles.filmFrameCenter} />
              <span className={styles.filmFrameSide} />
            </div>
            <p className={styles.phoneTitle}>
              {generatingMicroreelText.generatingYourReels}
            </p>
            <p className={styles.phoneHint}>
              {generatingMicroreelText.generatingHint1}
              <br />
              {generatingMicroreelText.generatingHint2}
            </p>
            {checkFailures >= CHECK_FAILURE_LIMIT && (
              <div className={styles.checkFailed}>
                <p className={styles.checkFailedText}>
                  {generatingMicroreelText.checkFailed}
                </p>
                <button
                  className={styles.checkFailedButton}
                  onClick={onRecheck}>
                  {generatingMicroreelText.checkFailedRetry}
                </button>
              </div>
            )}
            {canCancel && (
              <button
                className={styles.cancelGenerationButton}
                onClick={() => setConfirmCancelOpen(true)}
                disabled={canceling}>
                {generatingMicroreelText.cancelGeneration}
              </button>
            )}
          </div>

          <div className={styles.details}>
            <div className={styles.steps}>{STEPS.map(renderStep)}</div>
            <div className={styles.divider} />

            {info && (
              <>
                <h2 className={styles.detailsTitle}>{info.title}</h2>
                {info.description && (
                  <p className={styles.detailsDescription}>
                    {info.description}
                  </p>
                )}

                <div className={styles.settingsCard}>
                  <h3 className={styles.settingsTitle}>
                    {generatingMicroreelText.settingsUsed}
                  </h3>
                  <div className={styles.settingsGrid}>
                    {renderSetting(
                      generatingMicroreelText.labelLength,
                      info.lengthLabel,
                      generatingMicroreelText.hintLength,
                    )}
                    {renderSetting(
                      generatingMicroreelText.labelLanguage,
                      info.languageLabel,
                      info.languageHint ??
                        (info.voiceName === generateMicroreelText.voiceOriginal
                          ? generateMicroreelText.languageOnScreenHint
                          : generatingMicroreelText.hintLanguage),
                    )}
                    {renderSetting(
                      generatingMicroreelText.labelVoice,
                      info.voiceName,
                      info.voiceHint ?? generatingMicroreelText.hintVoice,
                    )}
                    {renderSetting(
                      generatingMicroreelText.labelVisuals,
                      info.visualsLabel ?? generatingMicroreelText.valueVisuals,
                      info.visualsHint ?? generatingMicroreelText.hintVisuals,
                    )}
                    {info.sourceFileName &&
                      renderSetting(
                        generatingMicroreelText.source,
                        info.sourceFileName,
                        `${
                          info.sourcePages
                            ? `${info.sourcePages} ${generatingMicroreelText.pages} · `
                            : ''
                        }${info.sourceFileSizeMb} MB`,
                      )}
                    {renderSetting(
                      generatingMicroreelText.labelCredits,
                      `${info.credits.toLocaleString('en-US')} ${
                        generatingMicroreelText.credits
                      }`,
                      generatingMicroreelText.hintCredits,
                    )}
                  </div>
                </div>

                {info.excludedPages && (
                  <p className={styles.excluded}>
                    {generatingMicroreelText.excludedPages} {info.excludedPages}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        {confirmCancelOpen && (
          <ConfirmationModal
            shown
            onAccept={onConfirmCancel}
            onCancel={() => setConfirmCancelOpen(false)}
            title={generatingMicroreelText.cancelConfirm}
            acceptText={generatingMicroreelText.cancelConfirmAccept}
          />
        )}
      </div>
    );
  }),
);

export default GeneratingMicroreel;
